// Shared XHR-based upload helper (Card #3492).
//
// Plain fetch() has no upload-progress API -- XMLHttpRequest's
// upload.onprogress is the only broadly-supported way to get a live
// byte-level percentage while a multipart POST is in flight. This is
// foreground (page context) ONLY -- see supportsUploadProgress() below for
// why the service worker's Background Sync replay must never use this.
//
// Used by field_image_queue.js's _uploadImage (the queued-image
// retry/replay path, shared with the service worker -- see that file's own
// context guard before calling in).

// True only in a page context with a working upload-progress API -- i.e.
// NEVER inside the service worker. Checked at call time, not cached at
// module load, so the same module behaves correctly if it's ever
// (accidentally or deliberately) evaluated in both contexts.
//
// Two independent reasons this stays foreground-only, not just one:
//   1. There is no UI to show a percentage to during a headless Background
//      Sync replay -- the tasker isn't looking at a screen.
//   2. XMLHttpRequest support inside a service worker is historically patchy
//      across browsers (unlike fetch, which every SW implementation
//      supports) -- not a risk worth taking for a visibility-only feature.
export function supportsUploadProgress() {
  return typeof window !== "undefined" && typeof XMLHttpRequest !== "undefined"
}

// Runs one multipart POST via XMLHttpRequest, reporting live progress via
// onProgress(loaded, total) whenever the browser can compute it (FormData
// bodies are lengthComputable in every browser this app supports).
//
// Mirrors fetch()'s throw-on-network-failure contract -- callers' existing
// catch blocks (retry/backoff logic in field_image_queue.js) keep working
// completely unchanged, since whatever is thrown here is treated identically
// to a fetch() rejection there. Resolves to a Response-like object (status,
// ok, statusText, url, headers.get(), json(), clone()) -- exactly what
// classifySyncResponse (field_sync_shared.js) needs, and nothing more.
export function uploadWithProgress(url, formData, { csrfToken, timeoutMs, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("POST", url)
    if (timeoutMs) xhr.timeout = timeoutMs
    if (csrfToken) xhr.setRequestHeader("X-CSRF-Token", csrfToken)
    xhr.setRequestHeader("Accept", "application/json")

    if (xhr.upload && typeof onProgress === "function") {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded, event.total)
      }
    }

    // Card #3798 -- root cause of the never-draining iOS queue: this promise
    // used to settle ONLY via onload/onerror/ontimeout. If the browser ever
    // aborts the underlying request without firing one of those three (page
    // navigation, backgrounding, iOS Safari suspending the tab mid-request --
    // all far more common on iOS standalone PWAs than Android), nothing here
    // ever resolved or rejected. The promise hung forever, silently -- no
    // downstream catch ever got a chance to log a failure, which is exactly
    // why _uploadImage's records sat stuck in "uploading" with zero trace.
    // `settled` makes every settle path idempotent so onloadend (which always
    // fires last, after any of the other four) can act as a final guard
    // without ever double-settling an already-resolved/rejected promise.
    let settled = false
    const settle = (fn, value) => {
      if (settled) return
      settled = true
      fn(value)
    }

    xhr.onload    = () => settle(resolve, _responseLike(xhr))
    xhr.onerror   = () => settle(reject, new TypeError("Failed to fetch"))
    xhr.ontimeout = () => settle(reject, Object.assign(new Error("Upload timed out"), { name: "TimeoutError" }))
    xhr.onabort   = () => settle(reject, Object.assign(new Error("Upload aborted"), { name: "AbortError" }))
    // Last-resort guard: loadend fires after load/error/abort/timeout, always,
    // per the XHR spec -- so if some future browser quirk ever fires it
    // without any of the four above firing first, the promise still settles
    // here rather than hanging silently forever again. Named AbortError (not
    // a bespoke name) so field_network_health's isTimeoutError classifier --
    // which already treats AbortError as a connectivity-quality signal --
    // picks this up the same way it does a genuine abort.
    xhr.onloadend = () => settle(reject, Object.assign(new Error("Upload ended without a result"), { name: "AbortError" }))

    xhr.send(formData)
  })
}

// Wraps a completed XMLHttpRequest just enough to satisfy classifySyncResponse
// and every existing !response.ok / response.json() call site. Unlike a real
// fetch Response, json()/clone() here are safely callable more than once --
// xhr.responseText is a plain string already fully in memory, not a one-shot
// stream.
function _responseLike(xhr) {
  const parse = () => {
    try { return JSON.parse(xhr.responseText) } catch { return undefined }
  }
  const responseLike = {
    status:     xhr.status,
    statusText: xhr.statusText,
    ok:         xhr.status >= 200 && xhr.status < 300,
    url:        xhr.responseURL || "",
    headers:    { get: (name) => xhr.getResponseHeader(name) },
    async json() {
      const parsed = parse()
      if (parsed === undefined) throw new Error("Invalid JSON body")
      return parsed
    }
  }
  responseLike.clone = () => responseLike
  return responseLike
}
